import React, { useState, useEffect } from 'react';
import { COUNTRIES_WITH_UK_FIRST } from '../constants/countries';
import { useNavigate } from 'react-router-dom';
import {
  getAllGroups,
  getAllUsers,
  getUserGroups,
  createGroup,
  addUser,
  addCredits,
  getCreditUsage,
  updateUserGroup,
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
} from '../utils/api';
import './AdminDashboard.css';
import Navbar from '../components/Navbar';

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('adminActiveTab') || 'overview';
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState({});
  const [products, setProducts] = useState([]);

  // Usage stats
  const [usageGroupId, setUsageGroupId] = useState('');
  const [usagePeriod, setUsagePeriod] = useState('30d');
  const [usageRows, setUsageRows] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState('');

  // Form states
  const [newGroup, setNewGroup] = useState({
    newGroupName: '',
    addressLine1: '',
    addressLine2: '',
    postcode: '',
    city: '',
    country: '',
    companyTaxCode: '',
    vat: '',
  });

  const [newUser, setNewUser] = useState({ username: '', group_id: '' });
  const [creditForm, setCreditForm] = useState({ groupId: '', credits: '' });
  const [userGroupForm, setUserGroupForm] = useState({
    userId: '',
    groupId: '',
  });
  const [newProduct, setNewProduct] = useState({
    code: '',
    name: '',
    description: '',
  });
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('adminActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    setUsageRows(null);
    setUsageError('');
    if (activeTab !== 'overview' || !usageGroupId) return;

    // Drop stale responses if the group or period changes mid-request.
    let cancelled = false;
    setUsageLoading(true);
    getCreditUsage(usagePeriod, usageGroupId)
      .then((data) => !cancelled && setUsageRows(data))
      .catch((err) => !cancelled && setUsageError(err.message || 'Failed to load usage'))
      .finally(() => !cancelled && setUsageLoading(false));

    return () => {
      cancelled = true;
    };
  }, [activeTab, usageGroupId, usagePeriod]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'overview' || activeTab === 'groups' || activeTab === 'users') {
        const groupsData = await getAllGroups();
        setGroups(groupsData.groups || []);
      }
      if (activeTab === 'overview' || activeTab === 'users') {
        const usersData = await getAllUsers();
        const userGroupsData = await getUserGroups();
        setUsers(usersData.users || []);
        setUserGroups(userGroupsData.userGroups || {});
      }
      if (activeTab === 'products') {
        const productsData = await listProducts();
        setProducts(productsData.products || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await createGroup(newGroup);
      setSuccess('Group created successfully');
      setNewGroup({
        newGroupName: '',
        addressLine1: '',
        addressLine2: '',
        postcode: '',
        city: '',
        country: '',
        companyTaxCode: '',
        vat: '',
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await addUser(newUser);
      setSuccess('User added successfully');
      setNewUser({ username: '', group_id: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddCredits = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await addCredits(creditForm.groupId, parseInt(creditForm.credits));
      setSuccess('Credits added successfully');
      setCreditForm({ groupId: '', credits: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateUserGroup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await updateUserGroup(userGroupForm.userId, userGroupForm.groupId);
      setSuccess('User group updated successfully');
      setUserGroupForm({ userId: '', groupId: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await createProduct(newProduct);
      setSuccess('Product created successfully');
      setNewProduct({ code: '', name: '', description: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await updateProduct(editingProduct.id, {
        name: editingProduct.name,
        description: editingProduct.description,
      });
      setSuccess('Product updated successfully');
      setEditingProduct(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    setError('');
    setSuccess('');
    try {
      await deleteProduct(productId);
      setSuccess('Product deleted successfully');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatCredits = (credits) => {
    return `£${(credits / 100).toFixed(2)}`;
  };

  const usageList = usageRows || [];
  const usageTotalCredits = usageList.reduce((sum, r) => sum + r.credits_consumed, 0);
  const usageTotalTransactions = usageList.reduce((sum, r) => sum + r.transaction_count, 0);
  const usageMessage = !usageGroupId
    ? 'Select a group to view its usage.'
    : usageError ||
      (usageLoading || !usageRows
        ? 'Loading...'
        : usageList.length === 0
          ? 'No usage recorded for this period.'
          : null);

  const USAGE_PERIODS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
  ];

  return (
    <div className='admin-dashboard'>
      <Navbar isAdminView />

      <div className='admin-content'>
        <div className='admin-sidebar'>
          <button
            className={`sidebar-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <span className='sidebar-icon'>📊</span>
            Overview
          </button>
          <button
            className={`sidebar-item ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            <span className='sidebar-icon'>🏢</span>
            Groups
          </button>
          <button
            className={`sidebar-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <span className='sidebar-icon'>👥</span>
            Users
          </button>
          <button
            className={`sidebar-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            <span className='sidebar-icon'>📦</span>
            Products
          </button>
        </div>

        <main className='admin-main'>
          {error && <div className='alert alert-error'>{error}</div>}
          {success && <div className='alert alert-success'>{success}</div>}

          {activeTab === 'overview' && (
            <div className='admin-section'>
              <h2>Overview</h2>
              <div className='stats-grid'>
                <div className='stat-card'>
                  <div className='stat-label'>Total Groups</div>
                  <div className='stat-value'>{groups.length}</div>
                </div>
                <div className='stat-card'>
                  <div className='stat-label'>Total Users</div>
                  <div className='stat-value'>{Object.keys(userGroups).length}</div>
                </div>
                <div className='stat-card'>
                  <div className='stat-label'>Total Credits</div>
                  <div className='stat-value'>
                    {formatCredits(groups.reduce((sum, g) => sum + (g.credits_remaining || 0), 0))}
                  </div>
                </div>
              </div>

              <div className='section-divider'></div>

              <h3>Group Usage</h3>
              <div className='overview-header'>
                <select
                  className='overview-period-select'
                  value={usageGroupId}
                  onChange={(e) => setUsageGroupId(e.target.value)}
                >
                  <option value=''>Select group...</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <select
                  className='overview-period-select'
                  value={usagePeriod}
                  onChange={(e) => setUsagePeriod(e.target.value)}
                >
                  {USAGE_PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className='dash-table-container'>
                <table className='dash-table'>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Transactions</th>
                      <th>Credits Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageMessage ? (
                      <tr>
                        <td colSpan={3}>{usageMessage}</td>
                      </tr>
                    ) : (
                      usageList.map((row) => (
                        <tr key={row.service}>
                          <td>{row.service}</td>
                          <td>{row.transaction_count}</td>
                          <td className='credits-cell'>£{row.credits_consumed.toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {usageGroupId && usageRows && (
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td>{usageTotalTransactions}</td>
                        <td className='credits-cell'>£{usageTotalCredits.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <div className='section-divider'></div>

              <h3>Group Overview</h3>
              <div className='data-table-container'>
                <table className='data-table'>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Credits</th>
                      <th>Top-Up Enabled</th>
                      <th>Threshold</th>
                      <th>Top-Up Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.name}</td>
                        <td className='credits-cell'>{formatCredits(group.credits_remaining)}</td>
                        <td>{group.topup_enabled ? 'Yes' : 'No'}</td>
                        <td className='credits-cell'>{formatCredits(group.topup_threshold)}</td>
                        <td className='credits-cell'>{formatCredits(group.topup_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'groups' && (
            <div className='admin-section'>
              <h2>Manage Groups</h2>

              <div className='card'>
                <div className='card-header'>
                  <h3>Create New Group</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleCreateGroup} className='admin-form'>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>Company Name *</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.newGroupName}
                          onChange={(e) =>
                            setNewGroup({
                              ...newGroup,
                              newGroupName: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className='form-group'>
                        <label className='label'>Country *</label>

                        <select
                          className='input'
                          value={newGroup.country}
                          onChange={(e) => setNewGroup({ ...newGroup, country: e.target.value })}
                          required
                        >
                          <option value=''>Select country...</option>
                          {COUNTRIES_WITH_UK_FIRST.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>Address Line 1 *</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.addressLine1}
                          onChange={(e) =>
                            setNewGroup({
                              ...newGroup,
                              addressLine1: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className='form-group'>
                        <label className='label'>Address Line 2</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.addressLine2}
                          onChange={(e) =>
                            setNewGroup({
                              ...newGroup,
                              addressLine2: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>City *</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.city}
                          onChange={(e) => setNewGroup({ ...newGroup, city: e.target.value })}
                          required
                        />
                      </div>
                      <div className='form-group'>
                        <label className='label'>Postcode *</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.postcode}
                          onChange={(e) =>
                            setNewGroup({
                              ...newGroup,
                              postcode: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                    </div>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>Invoice Prefix *</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.companyTaxCode}
                          onChange={(e) =>
                            setNewGroup({ ...newGroup, companyTaxCode: e.target.value })
                          }
                          required
                        />
                        <small className='form-hint'>
                          This will be used as the prefix for invoice numbers, e.g., "INV-001" where
                          "INV" is the prefix.
                        </small>
                      </div>

                      <div className='form-group'>
                        <label className='label'>VAT Number *</label>
                        <input
                          type='text'
                          className='input'
                          value={newGroup.vat}
                          onChange={(e) => setNewGroup({ ...newGroup, vat: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <button type='submit' className='btn btn-primary'>
                      Create Group
                    </button>
                  </form>
                </div>
              </div>

              <div className='section-divider'></div>

              <div className='card'>
                <div className='card-header'>
                  <h3>Add Credits to Group</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleAddCredits} className='admin-form'>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>Group</label>
                        <select
                          className='input'
                          value={creditForm.groupId}
                          onChange={(e) =>
                            setCreditForm({
                              ...creditForm,
                              groupId: e.target.value,
                            })
                          }
                          required
                        >
                          <option value=''>Select group...</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name} (ID: {group.id})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className='form-group'>
                        <label className='label'>Credits (pence)</label>
                        <input
                          type='number'
                          className='input'
                          value={creditForm.credits}
                          onChange={(e) =>
                            setCreditForm({
                              ...creditForm,
                              credits: e.target.value,
                            })
                          }
                          placeholder='5000'
                          required
                        />
                        <small className='form-hint'>
                          Enter amount in pence (e.g., 5000 = £50.00)
                        </small>
                      </div>
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Add Credits
                    </button>
                  </form>
                </div>
              </div>

              <div className='section-divider'></div>

              <h3>All Groups</h3>
              <div className='data-table-container'>
                <table className='data-table'>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.id}</td>
                        <td>{group.name}</td>
                        <td className='credits-cell'>{formatCredits(group.credits_remaining)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className='admin-section'>
              <h2>Manage Users</h2>

              <div className='card'>
                <div className='card-header'>
                  <h3>Add New User</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleAddUser} className='admin-form'>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>Email</label>
                        <input
                          type='email'
                          className='input'
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          placeholder='user@example.com'
                          required
                        />
                      </div>
                      <div className='form-group'>
                        <label className='label'>Group</label>
                        <select
                          className='input'
                          value={newUser.group_id}
                          onChange={(e) => setNewUser({ ...newUser, group_id: e.target.value })}
                          required
                        >
                          <option value=''>Select group...</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Add User
                    </button>
                  </form>
                </div>
              </div>

              <div className='section-divider'></div>

              <div className='card'>
                <div className='card-header'>
                  <h3>Update User Group</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleUpdateUserGroup} className='admin-form'>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>User</label>
                        <select
                          className='input'
                          value={userGroupForm.userId}
                          onChange={(e) =>
                            setUserGroupForm({
                              ...userGroupForm,
                              userId: e.target.value,
                            })
                          }
                          required
                        >
                          <option value=''>Select user...</option>
                          {Object.entries(userGroups).map(([userId, userData]) => (
                            <option key={userId} value={userId}>
                              {userData.username}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className='form-group'>
                        <label className='label'>New Group</label>
                        <select
                          className='input'
                          value={userGroupForm.groupId}
                          onChange={(e) =>
                            setUserGroupForm({
                              ...userGroupForm,
                              groupId: e.target.value,
                            })
                          }
                          required
                        >
                          <option value=''>Select group...</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Update Group
                    </button>
                  </form>
                </div>
              </div>

              <div className='section-divider'></div>

              <h3>All Users & Groups</h3>
              <div className='data-table-container'>
                <table className='data-table'>
                  <thead>
                    <tr>
                      <th>User ID</th>
                      <th>Email</th>
                      <th>Groups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(userGroups).map(([userId, userData]) => (
                      <tr key={userId}>
                        <td>{userId}</td>
                        <td>{userData.username}</td>
                        <td>
                          <div className='groups-list'>
                            {userData.groups.map((group, idx) => (
                              <span key={idx} className='badge badge-primary'>
                                {group.group_name}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div className='admin-section'>
              <h2>Manage Products</h2>

              <div className='card'>
                <div className='card-header'>
                  <h3>Create New Product</h3>
                </div>
                <div className='card-body'>
                  <form onSubmit={handleCreateProduct} className='admin-form'>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label className='label'>Product Code *</label>
                        <input
                          type='text'
                          className='input'
                          value={newProduct.code}
                          onChange={(e) =>
                            setNewProduct({
                              ...newProduct,
                              code: e.target.value,
                            })
                          }
                          placeholder='PROD-001'
                          maxLength={50}
                          required
                        />
                      </div>
                      <div className='form-group'>
                        <label className='label'>Product Name *</label>
                        <input
                          type='text'
                          className='input'
                          value={newProduct.name}
                          onChange={(e) =>
                            setNewProduct({
                              ...newProduct,
                              name: e.target.value,
                            })
                          }
                          placeholder='My Product'
                          maxLength={100}
                          required
                        />
                      </div>
                    </div>
                    <div className='form-group'>
                      <label className='label'>Description</label>
                      <textarea
                        className='input'
                        value={newProduct.description}
                        onChange={(e) =>
                          setNewProduct({
                            ...newProduct,
                            description: e.target.value,
                          })
                        }
                        placeholder='Product description...'
                        rows={3}
                        maxLength={500}
                      />
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Create Product
                    </button>
                  </form>
                </div>
              </div>

              <div className='section-divider'></div>

              <h3>All Products</h3>
              {editingProduct && (
                <div className='card edit-card'>
                  <div className='card-header'>
                    <h3>Edit Product</h3>
                  </div>
                  <div className='card-body'>
                    <form onSubmit={handleUpdateProduct} className='admin-form'>
                      <div className='form-group'>
                        <label className='label'>Product Name *</label>
                        <input
                          type='text'
                          className='input'
                          value={editingProduct.name}
                          onChange={(e) =>
                            setEditingProduct({
                              ...editingProduct,
                              name: e.target.value,
                            })
                          }
                          maxLength={100}
                          required
                        />
                      </div>
                      <div className='form-group'>
                        <label className='label'>Description</label>
                        <textarea
                          className='input'
                          value={editingProduct.description}
                          onChange={(e) =>
                            setEditingProduct({
                              ...editingProduct,
                              description: e.target.value,
                            })
                          }
                          rows={3}
                          maxLength={500}
                        />
                      </div>
                      <div className='form-actions'>
                        <button type='submit' className='btn btn-primary'>
                          Save Changes
                        </button>
                        <button
                          type='button'
                          className='btn btn-secondary'
                          onClick={() => setEditingProduct(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              <div className='data-table-container'>
                <table className='data-table'>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id}>
                        <td>{product.id}</td>
                        <td>{product.name}</td>
                        <td>{product.description || '-'}</td>
                        <td>
                          <div className='action-buttons'>
                            <button
                              className='btn btn-ghost btn-sm'
                              onClick={() => setEditingProduct(product)}
                            >
                              Edit
                            </button>
                            <button
                              className='btn btn-ghost btn-sm btn-danger'
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
